'use client';

import { c as _c } from "react/compiler-runtime";
import { canRetryFundingAuthentication, fundingApplePayButtonSettings, fundingPaymentScreen } from '@morfi/onramp-sdk';
import type { FundingCheckout } from '@morfi/onramp-sdk/react';
import type { ApplePayEvent, AuthEvent, BuyEvent, ChallengeEvent, ConnectEvent, GooglePayEvent, MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useEffectEvent, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { TOAST_IDS } from '@/config/toast-ids';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
interface Props {
  provider: Pick<MoonPayWebProvider, 'connect' | 'setupAuth' | 'setupChallenge' | 'setupApplePay' | 'setupGooglePay' | 'setupBuy'>;
  checkout: Pick<FundingCheckout, 'state' | 'authenticationReason' | 'method' | 'attemptId' | 'challengeUrl' | 'paymentSignature' | 'connected' | 'paymentEvent' | 'paymentFrameDetached' | 'paymentFrameReady' | 'challengeEvent'>;
  mode?: 'payment';
}
export function FundingProviderFrame(props) {
  const $ = _c(4);
  const [revision, setRevision] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setRevision(_temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props || $[2] !== revision) {
    t1 = <ProviderFrame key={revision} {...props} onRetry={t0} />;
    $[1] = props;
    $[2] = revision;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function _temp(current) {
  return current + 1;
}
function ProviderFrame({
  provider,
  checkout,
  onRetry,
  mode
}: Props & {
  onRetry(): void;
}): ReactNode {
  const container = useRef<HTMLDivElement>(null);
  const connected = useEffectEvent((status: Parameters<FundingCheckout['connected']>[0], customerId?: string): void => checkout.connected(status, customerId));
  const paymentEvent = useEffectEvent((event: ApplePayEvent | GooglePayEvent | BuyEvent): void => checkout.paymentEvent(event));
  const paymentFrameReady = useEffectEvent((updateQuote: (signature: string) => void): void => checkout.paymentFrameReady(updateQuote));
  const paymentFrameDetached = useEffectEvent((): void => checkout.paymentFrameDetached());
  const challengeEvent = useEffectEvent((event_0: ChallengeEvent): void => checkout.challengeEvent(event_0));
  const [failed, setFailed] = useState(false);
  const [rejected, setRejected] = useState(false);
  const t = useTranslations('onramp');
  const authenticationRejected = useEffectEvent((): void => {
    setRejected(true);
    setFailed(true);
    showToast({
      id: TOAST_IDS.ONRAMP_AUTH_REJECTED,
      message: t('verification_failed'),
      variant: 'error'
    });
    logger.warn('onramp.authentication_rejected', {
      phase: 'connect'
    });
  });
  const {
    method,
    attemptId
  } = checkout;
  const state = mode ?? checkout.state;
  const authenticationReason = state === 'connect' ? checkout.authenticationReason : null;
  const challengeUrl = state === 'challenge' ? checkout.challengeUrl : null;
  const signature = useRef(checkout.paymentSignature);
  const paymentFailed = useEffectEvent((): void => checkout.paymentEvent({
    kind: 'error',
    payload: {
      kind: 'genericError',
      message: 'frame_unavailable'
    }
  }));
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    // MoonPay connect resolves only after auth. Own the DOM before its handle exists.
    const mountPoint = document.createElement('div');
    mountPoint.className = 'h-full w-full';
    element.appendChild(mountPoint);
    let active = true;
    let authRejected = false;
    let frame: {
      dispose(): void;
    } | undefined;
    async function mount(): Promise<void> {
      // StrictMode cleanup runs before this microtask; never launch its abandoned setup.
      await Promise.resolve();
      if (!active) return;
      try {
        const result = state === 'connect' ? await (authenticationReason === 'account_mismatch' ? provider.connect : provider.setupAuth)({
          container: mountPoint,
          onEvent: (event_1: ConnectEvent | AuthEvent): void => {
            if (!active) return;
            if (event_1.kind === 'complete') connected(event_1.payload.status, event_1.payload.status === 'active' ? event_1.payload.customer.id : undefined);else if (event_1.kind === 'error') {
              if (canRetryFundingAuthentication(event_1.payload)) {
                authRejected = true;
                authenticationRejected();
              } else fail();
            }
          }
        }) : state === 'challenge' && challengeUrl ? await provider.setupChallenge({
          container: mountPoint,
          url: challengeUrl,
          onEvent: (event_2: ChallengeEvent): void => {
            if (active) challengeEvent(event_2);
          }
        }) : state === 'payment' && signature.current && attemptId ? await (method === 'card' || method === 'sepa' ? provider.setupBuy : method && fundingPaymentScreen(method) === 'apple_pay' ? provider.setupApplePay : provider.setupGooglePay)({
          container: mountPoint,
          quote: signature.current,
          externalTransactionId: attemptId,
          ...(method && fundingPaymentScreen(method) === 'apple_pay' ? {
            applePayButtonSettings: fundingApplePayButtonSettings
          } : {}),
          onEvent: (event_3: ApplePayEvent | GooglePayEvent | BuyEvent): void => {
            if (active) paymentEvent(event_3);
          }
        }) : null;
        if (result?.ok) {
          if (active) {
            frame = result.value;
            if (state === 'payment' && 'setQuote' in result.value) {
              const updateQuote_0 = result.value.setQuote;
              if (typeof updateQuote_0 === 'function') paymentFrameReady((signature_0: string): void => updateQuote_0(signature_0));
            }
          } else result.value.dispose();
        } else if (result && !authRejected) fail();
      } catch {
        if (active) logger.warn('onramp.frame_setup_failed', {
          phase: state
        });
        fail();
      }
    }
    function fail(): void {
      if (!active) return;
      setFailed(true);
      logger.warn('onramp.frame_failed', {
        phase: state
      });
      if (state === 'payment') paymentFailed();
    }
    void mount();
    return () => {
      active = false;
      if (state === 'payment') paymentFrameDetached();
      mountPoint.remove();
      frame?.dispose();
    };
  }, [provider, state, method, attemptId, challengeUrl, authenticationReason]);
  return <div className="rr-block flex flex-col gap-3" data-private="true">
      {failed ? <p role="alert" className="text-body">
          {t(rejected ? 'verification_failed' : 'provider_unavailable')}
        </p> : null}
      <div ref={container} hidden={failed} className={state === 'payment' ? 'h-11 w-full overflow-hidden rounded-control' : 'h-[28rem] w-full'} />
      {failed && state !== 'payment' ? <AnimatedButton variant="secondary" onClick={onRetry}>
          {t('retry')}
        </AnimatedButton> : null}
      {failed && state === 'payment' ? <p className="text-caption text-foreground-secondary">{t('receipt_pending')}</p> : null}
    </div>;
}